import api from '@/lib/api';
import type {
  ApiResponse,
  MusterSession,
  CreateMusterSessionRequest,
  UpdateMusterSessionRequest,
  TransferCommandRequest,
  MusterSessionStatsResponse,
  MusterTimelineEvent,
  SessionListQuery,
  PaginationMeta,
  ClassCheckinView,
  UpdateStudentCheckinRequest,
  BulkStudentCheckinRequest,
  SyncCheckinsRequest,
  SyncCheckinsResponse,
  UpdateStaffCheckinRequest,
  BulkStaffCheckinRequest,
  UpdateZoneSweepRequest,
  StudentCheckin,
  StaffCheckin,
  ZoneSweepReport,
} from '@amuster/types';

export const musterService = {
  listSessions(query?: SessionListQuery) {
    return api.get<ApiResponse<MusterSession[]> & { meta: PaginationMeta }>('/muster/sessions', { params: query });
  },

  createSession(data: CreateMusterSessionRequest) {
    return api.post<ApiResponse<MusterSession>>('/muster/sessions', data);
  },

  getActiveSessions() {
    return api.get<ApiResponse<MusterSession[]>>('/muster/sessions/active');
  },

  getSession(id: string) {
    return api.get<ApiResponse<MusterSession>>(`/muster/sessions/${id}`);
  },

  updateSession(id: string, data: UpdateMusterSessionRequest) {
    return api.put<ApiResponse<MusterSession>>(`/muster/sessions/${id}`, data);
  },

  closeSession(id: string) {
    return api.post<ApiResponse<MusterSession>>(`/muster/sessions/${id}/close`);
  },

  abortSession(id: string) {
    return api.post<ApiResponse<MusterSession>>(`/muster/sessions/${id}/abort`);
  },

  transferCommand(id: string, data: TransferCommandRequest) {
    return api.post<ApiResponse<MusterSession>>(`/muster/sessions/${id}/transfer-command`, data);
  },

  getStats(id: string) {
    return api.get<ApiResponse<MusterSessionStatsResponse>>(`/muster/sessions/${id}/stats`);
  },

  getTimeline(id: string) {
    return api.get<ApiResponse<MusterTimelineEvent[]>>(`/muster/sessions/${id}/timeline`);
  },

  // Student Checkins
  getStudentCheckins(sessionId: string) {
    return api.get<ApiResponse<StudentCheckin[]>>(`/muster/sessions/${sessionId}/checkins`);
  },

  updateStudentCheckin(sessionId: string, studentId: string, data: UpdateStudentCheckinRequest) {
    return api.put<ApiResponse<StudentCheckin>>(`/muster/sessions/${sessionId}/checkins/${studentId}`, data);
  },

  bulkStudentCheckin(sessionId: string, data: BulkStudentCheckinRequest) {
    return api.post<ApiResponse<{ count: number }>>(`/muster/sessions/${sessionId}/checkins/bulk`, data);
  },

  syncCheckins(sessionId: string, data: SyncCheckinsRequest) {
    return api.post<ApiResponse<SyncCheckinsResponse>>(`/muster/sessions/${sessionId}/checkins/sync`, data);
  },

  getClassCheckins(sessionId: string, classId: string) {
    return api.get<ApiResponse<ClassCheckinView>>(`/muster/sessions/${sessionId}/classes/${classId}`);
  },

  // Staff Checkins
  getStaffCheckins(sessionId: string) {
    return api.get<ApiResponse<StaffCheckin[]>>(`/muster/sessions/${sessionId}/staff-checkins`);
  },

  updateStaffCheckin(sessionId: string, userId: string, data: UpdateStaffCheckinRequest) {
    return api.put<ApiResponse<StaffCheckin>>(`/muster/sessions/${sessionId}/staff-checkins/${userId}`, data);
  },

  bulkStaffCheckin(sessionId: string, data: BulkStaffCheckinRequest) {
    return api.post<ApiResponse<{ count: number }>>(`/muster/sessions/${sessionId}/staff-checkins/bulk`, data);
  },

  // Sweeps
  getSweeps(sessionId: string) {
    return api.get<ApiResponse<ZoneSweepReport[]>>(`/muster/sessions/${sessionId}/sweeps`);
  },

  updateSweep(sessionId: string, zoneId: string, data: UpdateZoneSweepRequest) {
    return api.put<ApiResponse<ZoneSweepReport>>(`/muster/sessions/${sessionId}/sweeps/${zoneId}`, data);
  },

  getBuildingSweeps(sessionId: string, buildingId: string) {
    return api.get<ApiResponse<ZoneSweepReport[]>>(`/muster/sessions/${sessionId}/sweeps/building/${buildingId}`);
  },
};
